import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useSearchParams, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, BarChart, Bar } from "recharts";
import { Loader2, Info, Calendar, Award, Layers, Target, Flag, TrendingUp as TrendingUpIcon, ClipboardList, GraduationCap, Film, Share2, Sparkles, Crosshair, ChevronDown, Brain, LayoutGrid, BarChart3, ArrowRightLeft } from "lucide-react";
import BasketballIcon from "../components/BasketballIcon";
import { watermarkWord } from "./PlayerHome";
import CollegeSourceEditor from "../components/CollegeSourceEditor";
import Navbar from "../components/Navbar";
import ShotChart from "../components/ShotChart";
import HeadshotUploader from "../components/HeadshotUploader";
import PlayerTrends from "../components/PlayerTrends";
import DevelopmentInventory from "../components/DevelopmentInventory";
import PlayerRoutines from "../components/PlayerRoutines";
import ShootingAssessment from "../components/shooting/ShootingAssessment";
import PlayerWorkoutLog from "../components/PlayerWorkoutLog";
import PlayerBio from "../components/PlayerBio";
import RecruitmentTracker from "../components/RecruitmentTracker";
import RecruitmentAspirations from "../components/RecruitmentAspirations";
import FilmReviews from "../components/FilmReviews";
import ShareAccessDialog from "../components/ShareAccessDialog";
import SportsVisioAnalytics from "../components/SportsVisioAnalytics";
import PlayerProjections from "../components/PlayerProjections";
import PlayerShotDiet from "../components/PlayerShotDiet";
import SeasonStatsTab from "../components/season-stats/SeasonStatsTab";
import ShootingEvaluation from "../components/shooting-eval/ShootingEvaluation";
import SnapshotListSection from "../components/inventory/SnapshotListSection";
import TrainingPlan from "../components/training/TrainingPlan";
import PlayerIQ from "../components/PlayerIQ";
import { api } from "../lib/api";
import { TrainingLevelEditor } from "../components/TrainingLevel";
import { useAuth, isAdmin, isFamily, isTrainer, useOrgAccent } from "../contexts/AuthContext";
import { can, visibleTabs } from "../lib/permissions";

function fmt(n, digits = 1) {
  if (n === null || n === undefined || n === "") return "—";
  if (typeof n === "number") return n.toFixed(digits);
  return n;
}
function pct(n) { return n === null || n === undefined ? "—" : `${(n * 100).toFixed(1)}%`; }

// Available seasons per league. NBA years = start year of season.
const NBA_SEASONS = [2025, 2024, 2023, 2022, 2021, 2020];
const NCAA_SEASONS = ["current", "2024", "2023", "2022"];

function seasonLabel(league, s) {
  if (league === "WNBA") return String(s);
  if (league === "NBA") {
    const y = Number(s);
    return `${y}-${String((y + 1) % 100).padStart(2, "0")}`;
  }
  if (s === "current") return "2025-26";
  const y = Number(s);
  return `${y}-${String((y + 1) % 100).padStart(2, "0")}`;
}

export default function PlayerDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const accent = useOrgAccent();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [multiSeason, setMultiSeason] = useState(null);
  const [multiLoading, setMultiLoading] = useState(false);
  const [showMulti, setShowMulti] = useState(false);
  const [shooting, setShooting] = useState(null);
  const [shootingLoading, setShootingLoading] = useState(false);
  const [heroBust, setHeroBust] = useState(0);
  const [shareOpen, setShareOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("analytics"); // "analytics" | "inventory" | "training" | "recruitment" | "film"

  // Auto-switch default tab:
  //  - ?tab=<key> query wins (used by MyPlayers to drop trainers on Training)
  //  - Trainer users land on Training by default
  //  - HS players and family users land on Development Inventory
  //  - Everyone else lands on Analytics
  // Runs only once per player load — user's manual tab clicks are preserved.
  const [tabInitializedFor, setTabInitializedFor] = useState(null);
  useEffect(() => {
    const lg = data?.player?.league;
    const tl = data?.player?.training_level;
    if (!lg || tabInitializedFor === id) return;
    const qs = (searchParams.get("tab") || "").toLowerCase();
    const allowed = visibleTabs(user, lg);
    // Shot Diet + Projections are hidden for HS / Program Platinum unless the viewer is Steve or Justin.
    const restrictedViewers = ["steve@maximizebasketball.com", "justin@maximizebasketball.com"];
    const isPlatinum = (tl || "").toLowerCase() === "the program platinum";
    const hideAdvanced =
      (lg === "HS" || isPlatinum) &&
      !restrictedViewers.includes((user?.email || "").toLowerCase());
    const isBlocked = (t) => !allowed.includes(t) || (hideAdvanced && (t === "shot_diet" || t === "projections"));
    let initial;
    if (qs && !isBlocked(qs)) initial = qs;
    else if (isTrainer(user)) initial = "training";
    else if (lg === "HS" || isFamily(user)) initial = "inventory";
    else initial = "analytics";
    if (isBlocked(initial)) initial = allowed.find(t => !isBlocked(t)) || "inventory";
    setActiveTab(initial);
    setTabInitializedFor(id);
  }, [data, id, tabInitializedFor, user, searchParams]);
  // In-page links (e.g. "Plan → Training tab" from the Inventory) navigate on the same player; follow them even when ?tab= is unchanged (location.key changes on every navigation).
  const location = useLocation();
  useEffect(() => {
    const qs = (searchParams.get("tab") || "").toLowerCase();
    if (tabInitializedFor === id && visibleTabs(user, data?.player?.league).includes(qs)) setActiveTab(qs);
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps
  // Tier preview (View as) can hide the current tab — fall back to the first visible one.
  useEffect(() => {
    if (!data?.player || tabInitializedFor !== id) return;
    const allowed = visibleTabs(user, data.player.league);
    if (!allowed.includes(activeTab)) setActiveTab(allowed[0] || "inventory");
  }, [user, data, activeTab, tabInitializedFor, id]);

  // Initial + season change load
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const d = await api.playerDetails(id, selectedSeason || undefined);
        setData(d);
      } catch (e) {
        setError(e?.response?.data?.detail || e.message);
      } finally { setLoading(false); }
    })();
  }, [id, selectedSeason]);

  const league = data?.player?.league;
  // College players get server-computed season tabs (each knows which team / source applies — transfers keep prior seasons).
  const seasonTabs = data?.seasons || null;
  const availableSeasons = league === "NBA" ? NBA_SEASONS : (seasonTabs ? seasonTabs.map(t => t.key) : NCAA_SEASONS);
  const multiTeam = !!seasonTabs && new Set(seasonTabs.map(t => t.team)).size > 1;

  // Load multi-season comparison when opened
  useEffect(() => {
    if (!showMulti || !league) return;
    (async () => {
      setMultiLoading(true);
      try {
        const seasons = availableSeasons.slice(0, 4).map(String);
        const r = await api.playerMultiSeason(id, seasons);
        setMultiSeason(r);
      } catch (_) {
        setMultiSeason(null);
      } finally { setMultiLoading(false); }
    })();
  }, [showMulti, league, id]);

  // Load advanced shooting when player detail loaded (NBA only)
  useEffect(() => {
    if (!data || data.player?.league !== "NBA") { setShooting(null); return; }
    setShootingLoading(true);
    api.shooting(id).then(setShooting).catch(() => setShooting(null)).finally(() => setShootingLoading(false));
  }, [id, data]);

  const chartData = useMemo(() => {
    if (!data?.game_logs) return [];
    return data.game_logs.map((g, i) => ({
      idx: i + 1,
      date: g.date ? g.date.slice(5, 10) : `G${i + 1}`,
      PTS: g.pts ?? 0,
      REB: g.reb ?? 0,
      AST: g.ast ?? 0,
    }));
  }, [data]);

  const rollingAvg = useMemo(() => {
    if (!chartData.length) return [];
    let sp = 0, sr = 0, sa = 0;
    return chartData.map((d, i) => {
      sp += d.PTS; sr += d.REB; sa += d.AST;
      return { ...d, avgPTS: +(sp / (i + 1)).toFixed(1), avgREB: +(sr / (i + 1)).toFixed(1), avgAST: +(sa / (i + 1)).toFixed(1) };
    });
  }, [chartData]);

  // Trade splits — compute per-team averages when a player appears with 2+ teams in this season
  const tradeSplits = useMemo(() => {
    if (!data?.game_logs?.length) return null;
    const played = data.game_logs.filter(g => !g.dnp && g.team_abbr);
    if (played.length < 2) return null;
    const buckets = new Map();
    for (const g of played) {
      const key = g.team_abbr;
      if (!buckets.has(key)) buckets.set(key, { team_abbr: key, team: g.team, games: [] });
      buckets.get(key).games.push(g);
    }
    if (buckets.size < 2) return null;
    const parseMin = (m) => {
      if (m == null) return null;
      if (typeof m === "number") return m;
      const s = String(m);
      if (s.includes(":")) { const [a, b] = s.split(":"); return (parseFloat(a) || 0) + (parseFloat(b) || 0) / 60; }
      const v = parseFloat(s); return isNaN(v) ? null : v;
    };
    const avg = (arr, key) => {
      const vals = arr.map(g => g[key]).filter(v => v !== null && v !== undefined);
      return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
    };
    const rate = (arr, m, a) => {
      const mt = arr.reduce((s, g) => s + (g[m] || 0), 0);
      const at = arr.reduce((s, g) => s + (g[a] || 0), 0);
      return at ? mt / at : null;
    };
    // Order buckets chronologically by first game date
    const ordered = [...buckets.values()].sort((a, b) => {
      const da = a.games[0]?.date || "";
      const db = b.games[0]?.date || "";
      return da.localeCompare(db);
    });
    return ordered.map(({ team_abbr, team, games }) => {
      const minVals = games.map(g => parseMin(g.min)).filter(v => v != null);
      const minAvg = minVals.length ? minVals.reduce((a, b) => a + b, 0) / minVals.length : null;
      return {
        team_abbr, team,
        gp: games.length,
        first_date: games[0]?.date,
        last_date: games[games.length - 1]?.date,
        min: minAvg != null ? minAvg.toFixed(1) : null,
        pts: avg(games, "pts"),
        reb: avg(games, "reb"),
        ast: avg(games, "ast"),
        stl: avg(games, "stl"),
        blk: avg(games, "blk"),
        turnover: avg(games, "turnover"),
        fg_pct: rate(games, "fgm", "fga"),
        fg3_pct: rate(games, "fg3m", "fg3a"),
        ft_pct: rate(games, "ftm", "fta"),
      };
    });
  }, [data]);

  if (loading && !data) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="max-w-[1400px] mx-auto px-6 py-24 flex items-center justify-center text-[#a0a0a0]" data-testid="detail-loading">
          <Loader2 className="w-6 h-6 animate-spin mr-3" /> Loading player…
        </div>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="max-w-[1400px] mx-auto px-6 py-24 text-center">
          <div className="font-heading text-4xl uppercase text-white mb-2">Player not found</div>
          <p className="text-[#a0a0a0] mb-6">{error}</p>
          <Link to="/" className="text-[var(--org-accent)] uppercase text-xs tracking-[0.25em]">← Back to dashboard</Link>
        </div>
      </div>
    );
  }

  const { player, season_averages: avg, game_logs: logs = [], ncaa_note, season, stint } = data;
  const isNBA = player.league === "NBA";
  const currentSeasonKey = selectedSeason ?? (isNBA || seasonTabs ? String(season) : "current");
  const hasHeadshot = !!(data.headshot_url || player.nba_official_id || player.has_uploaded_headshot);
  const admin = isAdmin(user);
  const showUploader = admin && (!isNBA || !player.nba_official_id);

  const refreshDetails = () => {
    setHeroBust(k => k + 1);
    // re-fetch to update has_uploaded_headshot
    api.playerDetails(id, selectedSeason || undefined).then(setData).catch(() => {});
  };

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* HERO */}
      <section className="border-b border-white/10 relative overflow-hidden" data-testid="player-hero" style={{ background: "linear-gradient(180deg, #121212 0%, #0c0c0c 100%)" }}>
        <div className="absolute inset-0 pointer-events-none select-none" aria-hidden="true">
          <div className="absolute right-[-2%] top-2 font-heading uppercase whitespace-nowrap text-white/[0.035] leading-none" style={{ fontSize: "clamp(6rem, 18vw, 17rem)" }}>{watermarkWord((player.league === "HS" && player.bio?.hs_name) || player.team)}</div>
          <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "radial-gradient(circle at 12% 20%, #d8b854 0, transparent 35%), radial-gradient(circle at 88% 85%, #3aa0ff 0, transparent 40%)" }} />
          <div className="absolute inset-0" style={{ backgroundImage: "linear-gradient(to right, rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.03) 1px, transparent 1px)", backgroundSize: "40px 40px", maskImage: "linear-gradient(180deg, black, transparent 85%)", WebkitMaskImage: "linear-gradient(180deg, black, transparent 85%)" }} />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-8 lg:py-12">
          {/* Header row: small avatar + name + team */}
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="flex items-center gap-3 sm:gap-5"
            data-testid="hero-header"
          >
            <div className="relative w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 shrink-0 bg-gradient-to-b from-[#1c1c1c] to-[#141414] border border-white/15 overflow-hidden" data-testid="player-portrait-wrap">
              {hasHeadshot ? (
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/players/${player.id}/headshot?v=${heroBust}`}
                  alt={player.name}
                  className="absolute inset-0 w-full h-full object-cover object-top"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  data-testid="player-portrait"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="font-heading text-2xl text-white/25 uppercase">
                    {player.name?.split(' ').map(p => p[0]).slice(0, 2).join('')}
                  </div>
                </div>
              )}
              {/* gold accent bar */}
              <div className="absolute top-0 left-0 h-[3px] w-full bg-gradient-to-r from-[var(--org-accent)] via-[var(--org-accent)]/60 to-transparent" />
              {showUploader && (
                <div className="absolute bottom-1 right-1">
                  <HeadshotUploader
                    playerId={player.id}
                    hasUpload={player.has_uploaded_headshot}
                    variant="small"
                    onUpdated={refreshDetails}
                  />
                </div>
              )}
            </div>

            <div className="min-w-0">
              <div className="flex items-center gap-3 mb-1.5 flex-wrap">
                <span className={`inline-block text-[10px] font-bold tracking-[0.3em] uppercase px-2 py-1 ${isNBA ? "bg-[var(--org-accent)] text-[#121212]" : "border border-[var(--org-accent)] text-[var(--org-accent)]"}`} data-testid="detail-league-badge">
                  {player.league}
                </span>
                {player.jersey_number && (
                  <span className="font-mono text-xs text-[#a0a0a0]">#{player.jersey_number}</span>
                )}
                {player.position && <span className="text-xs uppercase tracking-widest text-[#a0a0a0]">{player.position}</span>}
                {season && <span className="text-[10px] text-[#757575] uppercase tracking-[0.3em]">· Season {player.league === "WNBA" ? season : `${season}–${String((Number(season) + 1) % 100).padStart(2, "0")}`}</span>}
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl md:text-6xl leading-[0.9] tracking-tight text-white uppercase break-words" data-testid="detail-player-name">
                {player.name}
              </h1>
              <div className="mt-1.5 flex items-center gap-2 sm:gap-3 flex-wrap">
                <div className="text-sm sm:text-base md:text-lg uppercase tracking-widest text-[var(--org-accent)]">{player.team}</div>
                <Link
                  to={`/home/${player.id}`}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-[#3aa0ff]/40 text-[10px] uppercase tracking-[0.25em] font-bold text-[#3aa0ff] hover:bg-[#3aa0ff]/10 hover:border-[#3aa0ff]"
                  data-testid="player-dashboard-link"
                >
                  <LayoutGrid className="w-3 h-3" /> Player Dashboard
                </Link>
                <TrainingLevelEditor
                  playerId={id}
                  level={player.training_level}
                  subTier={player.training_sub_tier}
                  onChange={(r) => setData(prev => prev ? ({ ...prev, player: { ...prev.player, training_level: r.training_level, training_sub_tier: r.training_sub_tier } }) : prev)}
                />
                {admin && (
                  <button
                    onClick={() => setShareOpen(true)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-white/15 text-[10px] uppercase tracking-[0.25em] font-bold text-[#a0a0a0] hover:text-white hover:border-[var(--org-accent)]"
                    style={{ transition: "color 0.2s ease, border-color 0.2s ease" }}
                    data-testid="share-access-btn"
                  >
                    <Share2 className="w-3 h-3" /> Share Access
                  </button>
                )}
              </div>
            </div>
          </motion.div>

        </div>
      </section>

      {/* TAB BAR — layout differs slightly for HS players (no analytics, adds recruitment + film) */}
      <section className="border-b border-white/10 bg-[#0a0a0a]/70 backdrop-blur-xl sticky top-14 sm:top-16 z-30" data-testid="player-tabs">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          {(() => {
            // Tier access comes from lib/permissions (the owner's Access Matrix). Shot Diet / Projections are additionally
            // hidden on HS or The Program Platinum players for everyone except Steve & Justin.
            const restrictedViewers = ["steve@maximizebasketball.com", "justin@maximizebasketball.com"];
            const isPlatinum = (player.training_level || "").toLowerCase() === "the program platinum";
            const hideAdvanced =
              (player.league === "HS" || isPlatinum) &&
              !restrictedViewers.includes((user?.email || "").toLowerCase());
            const allowed = visibleTabs(user, player.league);
            const tabs = [
                  { key: "inventory",   label: "Development Inventory", Icon: ClipboardList },
                  { key: "analytics",   label: "Analytics",             Icon: TrendingUpIcon },
                  { key: "season_stats", label: "Season Stats",         Icon: BarChart3 },
                  { key: "shot_diet",   label: "Shot Diet",             Icon: Crosshair },
                  { key: "film",        label: "Film Reviews",          Icon: Film },
                  { key: "iq",          label: "IQ",                    Icon: Brain },
                  { key: "training",    label: "Training",              Icon: BasketballIcon, iconIsComponent: true },
                  { key: "recruitment", label: "Recruitment",           Icon: GraduationCap },
                  { key: "projections", label: "Projections",           Icon: Sparkles },
            ].filter(t => allowed.includes(t.key) && !(hideAdvanced && (t.key === "shot_diet" || t.key === "projections")));
            const current = tabs.find(t => t.key === activeTab) || tabs[0];
            const CurrentIcon = current.Icon;
            return (
              <>
                {/* Mobile: dropdown select */}
                <div className="md:hidden py-3">
                  <div className="relative">
                    <div className="flex items-center gap-2 px-4 py-3 border border-[var(--org-accent)]/40 bg-[#171717]">
                      {current.iconIsComponent
                        ? <CurrentIcon size={16} className="text-[var(--org-accent)]" />
                        : <CurrentIcon className="w-4 h-4 text-[var(--org-accent)]" />}
                      <span className="text-xs uppercase tracking-[0.3em] font-bold text-[var(--org-accent)] flex-1">{current.label}</span>
                      <ChevronDown className="w-4 h-4 text-[var(--org-accent)]" />
                    </div>
                    <select
                      value={activeTab}
                      onChange={(e) => setActiveTab(e.target.value)}
                      className="absolute inset-0 opacity-0 cursor-pointer"
                      data-testid="mobile-tab-select"
                      aria-label="Select tab"
                    >
                      {tabs.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                    </select>
                  </div>
                </div>
                {/* Desktop: full tab bar */}
                <div className="hidden md:flex items-center gap-1 flex-wrap py-2">
                  {tabs.map(({ key, label, Icon, iconIsComponent }) => (
                    <button
                      key={key}
                      onClick={() => setActiveTab(key)}
                      className={`relative px-4 py-2.5 text-[11px] uppercase tracking-[0.25em] font-bold flex items-center gap-2 border ${
                        activeTab === key
                          ? "border-[#3aa0ff]/50 bg-[#3aa0ff]/10 text-white shadow-[0_0_18px_-6px_rgba(58,160,255,0.6)]"
                          : "border-transparent text-[#a0a0a0] hover:text-white hover:bg-white/[0.04]"
                      }`}
                      data-testid={`tab-${key}`}
                    >
                      {iconIsComponent ? <Icon size={14} className={activeTab === key ? "text-[#3aa0ff]" : ""} /> : <Icon className={`w-3.5 h-3.5 ${activeTab === key ? "text-[#3aa0ff]" : ""}`} />}
                      {label}
                      {activeTab === key && <span className="absolute left-3 right-3 -bottom-px h-[2px] bg-[#3aa0ff] shadow-[0_0_10px_#3aa0ff]" />}
                    </button>
                  ))}
                </div>
              </>
            );
          })()}
        </div>
      </section>

      {activeTab === "inventory" ? (
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-8 space-y-6 relative z-20">
          {player.league === "HS" && can(user, "inventory.bio") && <PlayerBio playerId={id} initialBio={player.bio} />}
          <DevelopmentInventory playerId={id} playerName={player.name} />
        </div>
      ) : activeTab === "recruitment" ? (
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-8 space-y-6">
          <RecruitmentAspirations playerId={id} />
          <RecruitmentTracker playerId={id} playerName={player.name} />
        </div>
      ) : activeTab === "film" ? (
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-8">
          <FilmReviews playerId={id} playerName={player.name} />
        </div>
      ) : activeTab === "iq" ? (
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-8">
          <PlayerIQ playerId={id} />
        </div>
      ) : activeTab === "training" ? (
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-8 space-y-6">
          {can(user, "training.priorities") && <TrainingPlan playerId={id} showPlan={can(user, "training.plan")} />}
          {can(user, "training.routines") && <PlayerRoutines playerId={id} playerName={player.name} />}
          {can(user, "training.workout_log") && <PlayerWorkoutLog playerId={id} playerName={player.name} />}
          {can(user, "training.shooting_assessment") && <ShootingAssessment playerId={id} playerName={player.name} context={[player.team, player.league || player.conference].filter(Boolean).join(" (") + (player.league || player.conference ? ")" : "")} />}
        </div>
      ) : activeTab === "projections" ? (
        <PlayerProjections seasonAverages={data?.season_averages} playerName={player.name} />
      ) : activeTab === "season_stats" ? (
        <SeasonStatsTab playerId={id} playerName={player.name} />
      ) : activeTab === "shot_diet" ? (
        <div>
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-8">
            <ShootingEvaluation playerId={id} playerName={player.name} seasonAverages={data?.season_averages} />
          </div>
          <PlayerShotDiet playerId={id} playerName={player.name} seasonAverages={data?.season_averages} />
        </div>
      ) : activeTab === "analytics" && player.league === "HS" ? (
        can(user, "analytics.hs_kpis") ? <SportsVisioAnalytics playerId={id} /> : <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 text-sm text-[#757575]" data-testid="hs-analytics-hidden">Game analytics for this player are shared by the coaching staff.</div>
      ) : (
      <>
      {admin && (player.league === "NCAA" || player.league === "WNCAA") && (
        <section className="border-b border-white/10">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-3">
            <CollegeSourceEditor key={`${player.division}-${player.school_site_url}`} player={player} sourceError={data.source_error} rosterOnly={data.roster_only} onSaved={refreshDetails} />
          </div>
        </section>
      )}
      {/* SEASON SELECTOR */}
      <section className="border-b border-white/10 bg-[#1c1c1c]/50" data-testid="season-selector">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-4 flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#a0a0a0]">Season</span>
            <div className="flex flex-wrap border border-white/10">
              {availableSeasons.map(s => {
                const key = String(s);
                const active = String(currentSeasonKey) === key;
                const tab = seasonTabs?.find(t => t.key === key);
                return (
                  <button
                    key={key}
                    onClick={() => setSelectedSeason(key)}
                    className={`px-3 py-2 text-[11px] font-mono border-r border-white/10 last:border-r-0 text-left ${
                      active ? "bg-[var(--org-accent)] text-[#121212] font-bold" : "text-[#a0a0a0] hover:text-white hover:bg-white/5"
                    }`}
                    style={{ transition: "background-color 0.15s ease, color 0.15s ease" }}
                    data-testid={`season-${key}`}
                    title={tab ? `${tab.team} · ${tab.division}` : undefined}
                  >
                    {tab ? tab.label : seasonLabel(player.league, s)}
                    {tab && multiTeam && <span className={`block text-[9px] uppercase tracking-[0.15em] font-sans ${active ? "text-[#121212]/70" : "text-[#757575]"}`} data-testid={`season-team-${key}`}>{tab.team}{tab.division !== "D1" ? ` · ${tab.division}` : ""}</span>}
                  </button>
                );
              })}
            </div>
            {loading && <Loader2 className="w-3 h-3 animate-spin text-[var(--org-accent)]" />}
          </div>
          <button
            onClick={() => setShowMulti(v => !v)}
            className={`flex items-center gap-2 px-4 py-2 text-[11px] uppercase tracking-[0.25em] font-bold border ${
              showMulti ? "bg-[var(--org-accent)] text-[#121212] border-[var(--org-accent)]" : "border-white/20 text-white hover:border-[var(--org-accent)] hover:text-[var(--org-accent)]"
            }`}
            style={{ transition: "background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease" }}
            data-testid="toggle-multi-season"
          >
            <Layers className="w-3 h-3" /> {showMulti ? "Hide History" : "Compare Seasons"}
          </button>
        </div>
      </section>

      {/* MULTI-SEASON PANEL */}
      {showMulti && (
        <section className="border-b border-white/10 bg-[#101010]" data-testid="multi-season-panel">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-8">
            <div className="flex items-center gap-3 mb-4">
              <Layers className="w-4 h-4 text-[var(--org-accent)]" />
              <div className="text-[10px] tracking-[0.4em] uppercase text-[#a0a0a0] font-bold">Season-by-Season</div>
              <div className="flex-1 h-px bg-white/10" />
            </div>
            {multiLoading ? (
              <div className="flex items-center gap-2 text-[#a0a0a0] text-sm py-8"><Loader2 className="w-4 h-4 animate-spin" /> loading history…</div>
            ) : !multiSeason ? (
              <div className="text-[#a0a0a0] text-sm">No historical data.</div>
            ) : (
              <MultiSeasonTable data={multiSeason} league={player.league} />
            )}
          </div>
        </section>
      )}

      {/* Transfer context */}
      {stint && multiTeam && (
        <div className="border-b border-white/10 bg-[#1c1c1c]">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-3 flex items-center gap-3 text-sm" data-testid="stint-banner">
            <ArrowRightLeft className="w-4 h-4 text-[var(--org-accent)]" />
            {data.auto_fallback
              ? <span className="text-[#a0a0a0]"><span className="text-white font-bold">{seasonTabs.find(t => t.key === String(data.auto_fallback.season))?.label}</span> at <span className="text-white font-bold">{data.auto_fallback.team}</span>{data.auto_fallback.division !== "D1" ? ` (${data.auto_fallback.division})` : ""} hasn't started{data.auto_fallback.roster_only ? " (he's on the roster)" : ""} — showing <span className="text-white font-bold">{seasonTabs.find(t => t.key === String(season))?.label}</span> with <span className="text-white font-bold">{stint.team}</span>.</span>
              : stint.current_team
              ? <span className="text-[#a0a0a0]">Showing <span className="text-white font-bold">{seasonTabs.find(t => t.key === String(season))?.label}</span> at <span className="text-white font-bold">{stint.team}</span>{stint.division !== "D1" ? ` (${stint.division})` : ""}{!avg ? " — season hasn't started yet. Prior seasons are in the tabs above." : ""}</span>
              : <span className="text-[#a0a0a0]">Showing <span className="text-white font-bold">{seasonTabs.find(t => t.key === String(season))?.label}</span> with <span className="text-white font-bold">{stint.team}</span> — he now plays for <span className="text-white font-bold">{player.team}</span>.</span>}
          </div>
        </div>
      )}

      {data.auto_prev_season && !(stint && multiTeam) && (
        <div className="border-b border-white/10 bg-[#1c1c1c]">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-2.5 text-xs text-[#a0a0a0]" data-testid="prev-season-banner">
            The new season hasn't tipped off yet — showing <span className="text-white font-bold">{seasonLabel(player.league, season)}</span> until games are played.
          </div>
        </div>
      )}

      {/* NCAA note */}
      {ncaa_note && !(stint && multiTeam && !stint.current_team) && (
        <div className="border-b border-white/10 bg-[#1c1c1c]">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-4 flex items-center gap-3 text-sm text-[#a0a0a0]" data-testid="ncaa-note">
            <Info className="w-4 h-4 text-[var(--org-accent)]" /> {ncaa_note}
          </div>
        </div>
      )}

      {/* CONTENT */}
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-10 space-y-10">

        {/* Extended stat tiles */}
        {avg && (
          <section data-testid="extended-stats">
            <SectionHead icon={<Award className="w-4 h-4" />} label="Season Averages" />
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-0 border border-white/10">
              <MicroStat label="GP" value={fmt(avg.games_played, 0)} />
              <MicroStat label="MIN" value={typeof avg.min === "number" ? fmt(avg.min) : (avg.min ?? "—")} />
              <MicroStat label="STL" value={fmt(avg.stl)} />
              <MicroStat label="BLK" value={fmt(avg.blk)} />
              <MicroStat label="TO" value={fmt(avg.turnover)} />
              <MicroStat label="OREB" value={fmt(avg.oreb)} />
              <MicroStat label="DREB" value={fmt(avg.dreb)} />
              <MicroStat label="FGM/A" value={`${fmt(avg.fgm)}/${fmt(avg.fga)}`} />
              <MicroStat label="3PM/A" value={`${fmt(avg.fg3m)}/${fmt(avg.fg3a)}`} />
              <MicroStat label="FTM/A" value={`${fmt(avg.ftm)}/${fmt(avg.fta)}`} />
              <MicroStat label="PF" value={fmt(avg.pf)} />
              <MicroStat label="PTS" value={fmt(avg.pts)} accent />
            </div>
          </section>
        )}

        {/* Trade splits — appears only for mid-season traded/transferred players */}
        {tradeSplits && tradeSplits.length >= 2 && (
          <section data-testid="trade-splits-section">
            <SectionHead icon={<Layers className="w-4 h-4" />} label={`Team Splits · ${tradeSplits.length} teams this season`} />
            <div className={`grid gap-4 ${tradeSplits.length === 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"}`}>
              {tradeSplits.map((s, i) => (
                <TradeSplitCard key={s.team_abbr} split={s} idx={i} />
              ))}
            </div>
          </section>
        )}

        {/* Player Trends — Last 5 vs Prior 5 vs Season */}
        {logs.length > 0 && (
          <section data-testid="trends-block">
            <SectionHead icon={<TrendingUpIcon className="w-4 h-4" />} label="Trending · Last 5 vs Prior 5 vs Season" />
            <PlayerTrends gameLogs={logs} window={5} seasonAverages={avg} />
          </section>
        )}

        {/* Trends chart (deprecated per user feedback) */}
        {false && chartData.length > 0 && (
          <section data-testid="trends-section">
            <SectionHead icon={<Calendar className="w-4 h-4" />} label="Trends · Points · Rebounds · Assists" />
            <div className="border border-white/10 bg-[#1c1c1c] p-4 h-[380px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={rollingAvg} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="#333333" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" stroke="#757575" fontSize={11} tick={{ fill: "#a0a0a0" }} />
                  <YAxis stroke="#757575" fontSize={11} tick={{ fill: "#a0a0a0" }} />
                  <Tooltip contentStyle={{ background: "#232323", border: "1px solid #d8b854", borderRadius: 0, color: "#fff", fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11, color: "#a0a0a0", textTransform: "uppercase", letterSpacing: "0.2em" }} />
                  <Line type="monotone" dataKey="PTS" stroke="#d8b854" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="REB" stroke="#ffffff" strokeWidth={1.5} dot={false} />
                  <Line type="monotone" dataKey="AST" stroke="#a0a0a0" strokeWidth={1.5} dot={false} />
                  <Line type="monotone" dataKey="avgPTS" stroke="#d8b854" strokeWidth={1} strokeDasharray="4 4" dot={false} name="PTS avg" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>
        )}

        {/* Distribution */}
        {chartData.length > 0 && (
          <section data-testid="distribution-section">
            <SectionHead label="Per-Game Points Distribution" />
            <div className="border border-white/10 bg-[#1c1c1c] p-4 h-[240px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid stroke="#333333" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" stroke="#757575" fontSize={11} tick={{ fill: "#a0a0a0" }} />
                  <YAxis stroke="#757575" fontSize={11} tick={{ fill: "#a0a0a0" }} />
                  <Tooltip contentStyle={{ background: "#232323", border: "1px solid #d8b854", borderRadius: 0, color: "#fff", fontSize: 12 }} />
                  <Bar dataKey="PTS" fill="#d8b854" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>
        )}

        {/* Advanced Shooting (NBA only) */}
        {isNBA && (shootingLoading || shooting?.supported) && (
          <section data-testid="advanced-shooting">
            <SectionHead icon={<Target className="w-4 h-4" />} label="Advanced Shooting" />
            {shootingLoading && !shooting ? (
              <div className="flex items-center gap-2 text-[#a0a0a0] text-sm py-8"><Loader2 className="w-4 h-4 animate-spin" /> loading shooting profile…</div>
            ) : shooting?.supported ? (
              <>
                {/* 3-Point block */}
                <ThreePointBlock tp={shooting.three_point} />

                {/* 2-Point block */}
                <TwoPointBlock sp={shooting.two_point} />

                {/* Shot chart */}
                <ShotChart zones={shooting.zones} />
                <div className="text-[10px] uppercase tracking-widest text-[#757575] mt-3">
                  Season: {shooting.season}-{String((shooting.season + 1) % 100).padStart(2, "0")} · 3-Point splits: balldontlie tracking · Zones: basketball-reference
                </div>
              </>
            ) : (
              <div className="border border-white/10 bg-[#1c1c1c] p-6 text-sm text-[#a0a0a0]">
                Advanced shooting profile not available for this player yet.
              </div>
            )}
          </section>
        )}

        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-8">
          <SnapshotListSection playerId={id} field="key_performance_indicators" title="Key Performance Indicators" subtitle="Dominate these areas for increased production and winning impact" icon={Target} numbered accent testid="kpi-section" />
        </section>

        {/* Game log table */}
        <section data-testid="game-log-section">
          <SectionHead label={`Game Log · ${logs.length} games`} />
          {logs.length === 0 ? (
            <div className="border border-white/10 bg-[#1c1c1c] p-10 text-center text-[#a0a0a0]" data-testid="no-games">
              {isNBA ? "No games this season yet." : "NCAA game-by-game data isn’t available via the public API."}
            </div>
          ) : (
            <div className="border border-white/10 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#101010] text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">
                  <tr>
                    {["Date", "Matchup", "Score", "MIN", "PTS", "REB", "AST", "STL", "BLK", "TO", "FG", "3P", "FT"].map(h => (
                      <th key={h} className="text-left px-3 py-3 border-b border-white/10 font-bold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="font-mono">
                  {logs.slice().reverse().map((g, i) => {
                    const home = g.home_team_score;
                    const away = g.visitor_team_score;
                    const rowBg = g.dnp
                      ? "bg-[#101010]/70"
                      : (i % 2 === 0 ? "bg-[#1c1c1c]" : "bg-[#101010]");
                    const textCol = g.dnp ? "text-[#757575]" : "text-white";
                    const matchup = g.matchup || (g.team_abbr || g.team || "—");
                    // Score with W/L based on player's team
                    let scoreText = "—";
                    if (home != null && away != null) {
                      if (g.is_home === true) {
                        scoreText = `${home}-${away}${!g.dnp ? (home > away ? " W" : " L") : ""}`;
                      } else if (g.is_home === false) {
                        scoreText = `${away}-${home}${!g.dnp ? (away > home ? " W" : " L") : ""}`;
                      } else {
                        scoreText = `${away}-${home}`;
                      }
                    }
                    return (
                      <tr key={i} className={`${rowBg} hover:bg-[#3c3c3c]`} style={{ transition: "background-color 0.15s ease" }} data-testid={`game-row-${i}`}>
                        <td className="px-3 py-2 border-b border-white/5 text-[#a0a0a0]">{g.date?.slice(0, 10) || "—"}</td>
                        <td className={`px-3 py-2 border-b border-white/5 ${textCol}`} data-testid={`matchup-${i}`}>{matchup}</td>
                        <td className="px-3 py-2 border-b border-white/5 text-[#a0a0a0]">{scoreText}</td>
                        {g.dnp ? (
                          <td colSpan={10} className="px-3 py-2 border-b border-white/5 text-[#757575] italic text-xs uppercase tracking-widest" data-testid={`dnp-${i}`}>
                            DNP — Did Not Play
                          </td>
                        ) : (
                          <>
                            <td className={`px-3 py-2 border-b border-white/5 ${textCol}`}>{g.min || "—"}</td>
                            <td className="px-3 py-2 border-b border-white/5 text-[var(--org-accent)] font-bold">{g.pts ?? "—"}</td>
                            <td className={`px-3 py-2 border-b border-white/5 ${textCol}`}>{g.reb ?? "—"}</td>
                            <td className={`px-3 py-2 border-b border-white/5 ${textCol}`}>{g.ast ?? "—"}</td>
                            <td className={`px-3 py-2 border-b border-white/5 ${textCol}`}>{g.stl ?? "—"}</td>
                            <td className={`px-3 py-2 border-b border-white/5 ${textCol}`}>{g.blk ?? "—"}</td>
                            <td className={`px-3 py-2 border-b border-white/5 ${textCol}`}>{g.turnover ?? "—"}</td>
                            <td className={`px-3 py-2 border-b border-white/5 ${textCol}`}>{g.fgm ?? "—"}/{g.fga ?? "—"}</td>
                            <td className={`px-3 py-2 border-b border-white/5 ${textCol}`}>{g.fg3m ?? "—"}/{g.fg3a ?? "—"}</td>
                            <td className={`px-3 py-2 border-b border-white/5 ${textCol}`}>{g.ftm ?? "—"}/{g.fta ?? "—"}</td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
      </>
      )}
      {admin && <ShareAccessDialog playerId={id} playerName={player.name} open={shareOpen} onClose={() => setShareOpen(false)} />}
    </div>
  );
}

function SectionHead({ label, icon }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      {icon && <span className="text-[var(--org-accent)]">{icon}</span>}
      <div className="text-[10px] tracking-[0.4em] uppercase text-[#a0a0a0] font-bold">{label}</div>
      <div className="flex-1 h-px bg-white/10" />
    </div>
  );
}



function MicroStat({ label, value, accent }) {
  return (
    <div className="p-4 border-r border-b border-white/10 last:border-r-0">
      <div className="text-[10px] tracking-[0.25em] uppercase text-[#a0a0a0] mb-1">{label}</div>
      <div className={`font-heading text-2xl leading-none ${accent ? "text-[var(--org-accent)]" : "text-white"}`}>{value}</div>
    </div>
  );
}

function TradeSplitCard({ split, idx }) {
  const p = (v) => (v === null || v === undefined) ? "—" : `${(v * 100).toFixed(1)}%`;
  const n = (v, d = 1) => (v === null || v === undefined) ? "—" : (typeof v === "number" ? v.toFixed(d) : v);
  const dateRange = split.first_date && split.last_date
    ? `${String(split.first_date).slice(5)}  →  ${String(split.last_date).slice(5)}`
    : "";
  return (
    <div
      className={`border ${idx === 0 ? "border-[var(--org-accent)]/60" : "border-white/15"} bg-[#1c1c1c] p-5`}
      data-testid={`trade-split-${split.team_abbr}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#a0a0a0]">
            {idx === 0 ? "First Stint" : idx === 1 ? "Current / Latest" : `Stint ${idx + 1}`}
          </div>
          <div className="font-heading text-2xl text-white uppercase leading-none mt-1">{split.team || split.team_abbr}</div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--org-accent)] mt-1">{split.team_abbr}</div>
        </div>
        <div className="text-right">
          <div className="text-[9px] uppercase tracking-widest text-[#757575]">GP</div>
          <div className="font-heading text-3xl text-[var(--org-accent)] leading-none">{split.gp}</div>
        </div>
      </div>
      {dateRange && (
        <div className="text-[10px] font-mono text-[#757575] mb-4 tracking-wider">{dateRange}</div>
      )}
      <div className="grid grid-cols-4 gap-0 border-t border-white/10">
        <MiniSplitStat label="MIN" value={split.min ?? "—"} />
        <MiniSplitStat label="PTS" value={n(split.pts)} accent />
        <MiniSplitStat label="REB" value={n(split.reb)} />
        <MiniSplitStat label="AST" value={n(split.ast)} />
        <MiniSplitStat label="STL" value={n(split.stl)} />
        <MiniSplitStat label="BLK" value={n(split.blk)} />
        <MiniSplitStat label="TO" value={n(split.turnover)} />
        <MiniSplitStat label="FG%" value={p(split.fg_pct)} />
        <MiniSplitStat label="3P%" value={p(split.fg3_pct)} />
        <MiniSplitStat label="FT%" value={p(split.ft_pct)} />
      </div>
    </div>
  );
}

function MiniSplitStat({ label, value, accent }) {
  return (
    <div className="p-2.5 border-r border-b border-white/10 last:border-r-0">
      <div className="text-[9px] tracking-[0.2em] uppercase text-[#a0a0a0]">{label}</div>
      <div className={`font-heading text-lg leading-tight ${accent ? "text-[var(--org-accent)]" : "text-white"}`}>{value}</div>
    </div>
  );
}

function ShotSplitCard({ title, catchShoot, offDribble, fgPct, fgLabel = "FG%", testid }) {
  const pct = (v) => (v === null || v === undefined ? "—" : `${(v * 100).toFixed(1)}%`);
  const csPct = (catchShoot || 0) * 100;
  const odPct = (offDribble || 0) * 100;
  return (
    <div className="border border-white/10 bg-[#1c1c1c] p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#a0a0a0]">{title}</div>
        <div className="text-right">
          <div className="text-[9px] uppercase tracking-widest text-[#757575]">{fgLabel}</div>
          <div className="font-heading text-2xl text-[var(--org-accent)]">{pct(fgPct)}</div>
        </div>
      </div>
      <div className="w-full h-6 bg-[#101010] flex overflow-hidden mb-3">
        <div className="bg-[var(--org-accent)] flex items-center justify-center" style={{ width: `${csPct}%` }}>
          {csPct > 15 && <span className="text-[10px] font-bold text-[#121212] tracking-widest">CATCH & SHOOT</span>}
        </div>
        <div className="bg-white/70 flex items-center justify-center" style={{ width: `${odPct}%` }}>
          {odPct > 15 && <span className="text-[10px] font-bold text-[#121212] tracking-widest">OFF DRIBBLE</span>}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="text-[9px] uppercase tracking-widest text-[#a0a0a0]">Catch & Shoot</div>
          <div className="font-heading text-3xl text-[var(--org-accent)] leading-none">{pct(catchShoot)}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-widest text-[#a0a0a0]">Off the Dribble</div>
          <div className="font-heading text-3xl text-white leading-none">{pct(offDribble)}</div>
        </div>
      </div>
    </div>
  );
}

function pctFmt(v) { return (v === null || v === undefined) ? "—" : `${(v * 100).toFixed(1)}%`; }

function ThreePointBlock({ tp }) {
  if (!tp) return null;
  const cs = tp.catch_shoot || {};
  const od = tp.off_dribble || {};
  const csUse = (cs.use_pct_of_3pa || 0) * 100;
  const odUse = (od.use_pct_of_3pa || 0) * 100;
  const trackingGp = tp.tracking_gp;
  const overallGp = tp.overall_gp;
  const mismatch = tp.sample_mismatch;
  return (
    <div className="border border-white/10 bg-[#1c1c1c] p-6 mb-4" data-testid="three-point-block">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#a0a0a0]">3-Point Shooting</div>
          {trackingGp != null && (
            <div className="text-[9px] uppercase tracking-widest text-[#757575] mt-1" data-testid="tracking-gp">
              Splits · Through {trackingGp} tracked {trackingGp === 1 ? "game" : "games"}
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="text-[9px] uppercase tracking-widest text-[#757575]">
            Overall 3P%{overallGp != null ? ` · ${overallGp}g` : ""}
          </div>
          <div className="font-heading text-4xl text-[var(--org-accent)] leading-none stat-glow" data-testid="overall-3p-pct">
            {pctFmt(tp.overall_3p_pct)}
          </div>
          <div className="text-[9px] uppercase tracking-widest text-[#757575] mt-1">
            {tp.total_3pa_per_game != null ? `${tp.total_3pa_per_game.toFixed?.(1) ?? tp.total_3pa_per_game} 3PA/g` : ""}
          </div>
        </div>
      </div>

      {mismatch && (
        <div className="mb-4 p-3 border border-amber-500/30 bg-amber-500/[0.06] text-[11px] leading-relaxed text-amber-200/90 flex gap-2" data-testid="tracking-mismatch-note">
          <span className="text-amber-400 shrink-0">⚠</span>
          <span>
            Balldontlie's shot-tracking feed is <strong>{overallGp - trackingGp} games behind</strong> the
            overall season averages. The Catch &amp; Shoot / Off-Dribble splits below reflect only the
            first {trackingGp} tracked games, so they will not blend into the season-long 3P% shown on the right.
          </span>
        </div>
      )}

      {/* Use% distribution bar */}
      <div className="w-full h-7 bg-[#101010] flex overflow-hidden mb-5">
        <div className="bg-[var(--org-accent)] flex items-center justify-center" style={{ width: `${csUse}%` }}>
          {csUse > 12 && <span className="text-[10px] font-bold text-[#121212] tracking-widest">C&S {csUse.toFixed(0)}%</span>}
        </div>
        <div className="bg-white/70 flex items-center justify-center" style={{ width: `${odUse}%` }}>
          {odUse > 12 && <span className="text-[10px] font-bold text-[#121212] tracking-widest">OFF DRIBBLE {odUse.toFixed(0)}%</span>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SplitTile
          label="Catch & Shoot"
          use={cs.use_pct_of_3pa}
          fga={cs.fga_per_game}
          fgPct={cs.fg_pct}
          accent
          testid="cs-3p"
        />
        <SplitTile
          label="Off the Dribble"
          use={od.use_pct_of_3pa}
          fga={od.fga_per_game}
          fgPct={od.fg_pct}
          testid="od-3p"
        />
      </div>
    </div>
  );
}

function SplitTile({ label, use, fga, fgPct, accent, testid }) {
  return (
    <div className="border border-white/10 bg-[#101010] p-4" data-testid={testid}>
      <div className="text-[10px] tracking-[0.3em] uppercase text-[#a0a0a0] mb-2">{label}</div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="text-[9px] uppercase tracking-widest text-[#757575]">Use %</div>
          <div className={`font-heading text-3xl leading-none ${accent ? "text-[var(--org-accent)]" : "text-white"}`} data-testid={`${testid}-use`}>
            {pctFmt(use)}
          </div>
          <div className="text-[9px] uppercase tracking-widest text-[#757575] mt-1">
            {fga != null ? `${(+fga).toFixed(1)} 3PA/g` : ""}
          </div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-widest text-[#757575]">3P%</div>
          <div className={`font-heading text-3xl leading-none ${accent ? "text-[var(--org-accent)]" : "text-white"}`} data-testid={`${testid}-pct`}>
            {pctFmt(fgPct)}
          </div>
        </div>
      </div>
    </div>
  );
}

function TwoPointBlock({ sp }) {
  if (!sp) return null;
  return (
    <div className="border border-white/10 bg-[#1c1c1c] p-6 mb-4" data-testid="two-point-block">
      <div className="text-[10px] tracking-[0.3em] uppercase text-[#a0a0a0] mb-4">2-Point Shooting</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TwoTile label="At the Rim" fgPct={sp.rim_fg_pct} share={sp.rim_pct_of_fga} accent testid="rim-2p" />
        <TwoTile label="Non-Rim" fgPct={sp.non_rim_fg_pct} share={sp.non_rim_pct_of_fga} testid="nonrim-2p" />
      </div>
    </div>
  );
}

function TwoTile({ label, fgPct, share, accent, testid }) {
  return (
    <div className="border border-white/10 bg-[#101010] p-4" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-3">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#a0a0a0]">{label}</div>
        <div className="text-[9px] uppercase tracking-widest text-[#757575]">
          {share != null ? `${(share * 100).toFixed(1)}% of FGA` : ""}
        </div>
      </div>
      <div className="text-[9px] uppercase tracking-widest text-[#757575]">FG %</div>
      <div className={`font-heading text-5xl leading-none ${accent ? "text-[var(--org-accent)] stat-glow" : "text-white"}`} data-testid={`${testid}-pct`}>
        {pctFmt(fgPct)}
      </div>
      <div className="w-full h-1.5 bg-[#141414] mt-3">
        <div className="h-full bg-[var(--org-accent)]" style={{ width: `${Math.max(2, (fgPct || 0) * 100)}%` }} />
      </div>
    </div>
  );
}

function MultiSeasonTable({ data, league }) {
  const rows = data?.seasons || [];
  const cols = [
    { key: "pts", label: "PPG", isPct: false },
    { key: "reb", label: "RPG", isPct: false },
    { key: "ast", label: "APG", isPct: false },
    { key: "stl", label: "SPG", isPct: false },
    { key: "blk", label: "BPG", isPct: false },
    { key: "fg_pct", label: "FG%", isPct: true },
    { key: "fg3_pct", label: "3P%", isPct: true },
    { key: "ft_pct", label: "FT%", isPct: true },
    { key: "games_played", label: "GP", isPct: false, isInt: true },
  ];

  function fmtCell(v, col) {
    if (v === null || v === undefined || v === "") return "—";
    if (col.isPct) {
      const n = Number(v);
      return Number.isNaN(n) ? String(v) : `${(n * 100).toFixed(1)}%`;
    }
    if (col.isInt) return String(v);
    const n = Number(v);
    return Number.isNaN(n) ? String(v) : n.toFixed(1);
  }

  const chartRows = rows.map(r => {
    const row = { season: /^\d{4}$/.test(String(r.season)) ? `${r.season}-${String((Number(r.season) + 1) % 100).padStart(2, "0")}` : r.season };
    cols.slice(0, 3).forEach(c => { row[c.label] = Number(r.season_averages?.[c.key]) || 0; });
    return row;
  }).reverse();

  return (
    <>
      <div className="border border-white/10 bg-[#1c1c1c] p-4 h-[240px] mb-6" data-testid="multi-season-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartRows}>
            <CartesianGrid stroke="#333333" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="season" stroke="#757575" fontSize={11} tick={{ fill: "#a0a0a0" }} />
            <YAxis stroke="#757575" fontSize={11} tick={{ fill: "#a0a0a0" }} />
            <Tooltip contentStyle={{ background: "#232323", border: "1px solid #d8b854", borderRadius: 0, color: "#fff", fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 11, color: "#a0a0a0", textTransform: "uppercase", letterSpacing: "0.2em" }} />
            <Bar dataKey="PPG" fill="#d8b854" />
            <Bar dataKey="RPG" fill="#ffffff" />
            <Bar dataKey="APG" fill="#a0a0a0" />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="border border-white/10 overflow-x-auto" data-testid="multi-season-table">
        <table className="w-full text-sm font-mono">
          <thead className="bg-[#101010] text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">
            <tr>
              <th className="text-left px-4 py-3 border-b border-white/10 font-bold">Season</th>
              {cols.map(c => (
                <th key={c.key} className="text-left px-4 py-3 border-b border-white/10 font-bold">{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.season} className={i % 2 === 0 ? "bg-[#1c1c1c]" : "bg-[#101010]"} data-testid={`ms-row-${r.season}`}>
                <td className="px-4 py-3 border-b border-white/5 text-[var(--org-accent)]">
                  {league === "NBA" ? `${r.season}-${String((Number(r.season) + 1) % 100).padStart(2, "0")}` : `${r.season}-${String((Number(r.season) + 1) % 100).padStart(2, "0")}`}
                  {r.team && <span className="block text-[9px] uppercase tracking-[0.15em] text-[#757575] font-sans">{r.team}</span>}
                </td>
                {cols.map(c => (
                  <td key={c.key} className="px-4 py-3 border-b border-white/5 text-white">
                    {fmtCell(r.season_averages?.[c.key], c)}
                  </td>
                ))}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={cols.length + 1} className="px-4 py-6 text-center text-[#757575]">No seasons found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
